import {
  FileQuestion,
  Search,
  AlertCircle,
  Sparkles,
  Plus,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react'
import { type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * 空状态类型
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
export type EmptyStateType = 'no-data' | 'no-results' | 'error' | 'first-time'

/**
 * 空状态配置
 */
interface EmptyStateConfig {
  icon: LucideIcon
  title: string
  description: string
  iconClassName?: string
}

const EMPTY_STATE_CONFIGS: Record<EmptyStateType, EmptyStateConfig> = {
  'no-data': {
    icon: FileQuestion,
    title: '暂无数据',
    description: '当前没有可显示的内容',
    iconClassName: 'text-muted-foreground',
  },
  'no-results': {
    icon: Search,
    title: '未找到结果',
    description: '尝试调整筛选条件或搜索关键词',
    iconClassName: 'text-muted-foreground',
  },
  error: {
    icon: AlertCircle,
    title: '加载失败',
    description: '无法加载数据，请稍后重试',
    iconClassName: 'text-destructive',
  },
  'first-time': {
    icon: Sparkles,
    title: '开始使用',
    description: '创建你的第一条记录',
    iconClassName: 'text-primary',
  },
}

/**
 * 空状态操作按钮
 */
export interface EmptyStateAction {
  label: string
  onClick: () => void
  variant?: 'default' | 'outline' | 'ghost'
  icon?: LucideIcon
}

/**
 * 空状态组件属性
 */
export interface EmptyStateProps {
  /** 空状态类型 */
  type: EmptyStateType
  /** 自定义标题 */
  title?: string
  /** 自定义描述 */
  description?: string
  /** 自定义图标 */
  icon?: LucideIcon
  /** 主要操作 */
  primaryAction?: EmptyStateAction
  /** 次要操作 */
  secondaryAction?: EmptyStateAction
  /** 自定义内容 */
  children?: ReactNode
  /** 自定义类名 */
  className?: string
  /** 图标大小 */
  iconSize?: 'sm' | 'md' | 'lg'
}

/**
 * 通用空状态组件
 * Requirements: 4.1, 4.2, 4.3
 */
export function EmptyState({
  type,
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  children,
  className,
  iconSize = 'md',
}: EmptyStateProps) {
  const config = EMPTY_STATE_CONFIGS[type]
  const Icon = icon ?? config.icon

  const iconSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
    >
      <div
        className={cn(
          'rounded-full bg-muted p-4 mb-4',
          type === 'error' && 'bg-destructive/10',
          type === 'first-time' && 'bg-primary/10'
        )}
      >
        <Icon className={cn(iconSizeClasses[iconSize], config.iconClassName)} />
      </div>

      <h3 className="text-lg font-semibold mb-2">{title ?? config.title}</h3>

      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {description ?? config.description}
      </p>

      {children}

      {(primaryAction || secondaryAction) && (
        <div className="flex items-center gap-3">
          {primaryAction && (
            <Button
              variant={primaryAction.variant ?? 'default'}
              onClick={primaryAction.onClick}
            >
              {primaryAction.icon && (
                <primaryAction.icon className="h-4 w-4 mr-2" />
              )}
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant ?? 'outline'}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.icon && (
                <secondaryAction.icon className="h-4 w-4 mr-2" />
              )}
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * 确定空状态类型
 * Property 5: Empty State Type Determination
 * Requirements: 4.4
 */
export function determineEmptyStateType(params: {
  hasData: boolean
  hasFilters: boolean
  hasError: boolean
  isFirstVisit: boolean
}): EmptyStateType {
  const { hasData, hasFilters, hasError, isFirstVisit } = params

  // 优先级: error > no-results > first-time > no-data
  if (hasError) {
    return 'error'
  }

  if (!hasData && hasFilters) {
    return 'no-results'
  }

  if (!hasData && isFirstVisit) {
    return 'first-time'
  }

  return 'no-data'
}

/**
 * 预设空状态组件 - 无数据
 */
export function NoDataEmptyState({
  title = '暂无数据',
  description = '当前没有可显示的内容',
  onCreateClick,
  createLabel = '创建',
  className,
}: {
  title?: string
  description?: string
  onCreateClick?: () => void
  createLabel?: string
  className?: string
}) {
  return (
    <EmptyState
      type="no-data"
      title={title}
      description={description}
      primaryAction={
        onCreateClick
          ? { label: createLabel, onClick: onCreateClick, icon: Plus }
          : undefined
      }
      className={className}
    />
  )
}

/**
 * 预设空状态组件 - 无搜索结果
 */
export function NoResultsEmptyState({
  title = '未找到结果',
  description = '尝试调整筛选条件或搜索关键词',
  onClearFilters,
  className,
}: {
  title?: string
  description?: string
  onClearFilters?: () => void
  className?: string
}) {
  return (
    <EmptyState
      type="no-results"
      title={title}
      description={description}
      primaryAction={
        onClearFilters
          ? { label: '清除筛选', onClick: onClearFilters, variant: 'outline' }
          : undefined
      }
      className={className}
    />
  )
}

/**
 * 预设空状态组件 - 错误
 */
export function ErrorEmptyState({
  title = '加载失败',
  description = '无法加载数据，请稍后重试',
  onRetry,
  className,
}: {
  title?: string
  description?: string
  onRetry?: () => void
  className?: string
}) {
  return (
    <EmptyState
      type="error"
      title={title}
      description={description}
      primaryAction={
        onRetry
          ? { label: '重试', onClick: onRetry, icon: RefreshCw }
          : undefined
      }
      className={className}
    />
  )
}

/**
 * 预设空状态组件 - 首次使用
 */
export function FirstTimeEmptyState({
  title = '开始使用',
  description = '创建你的第一条记录',
  onCreateClick,
  createLabel = '立即创建',
  className,
}: {
  title?: string
  description?: string
  onCreateClick?: () => void
  createLabel?: string
  className?: string
}) {
  return (
    <EmptyState
      type="first-time"
      title={title}
      description={description}
      primaryAction={
        onCreateClick
          ? { label: createLabel, onClick: onCreateClick, icon: Plus }
          : undefined
      }
      className={className}
    />
  )
}
